import Stack from "@mui/material/Stack";
import type { ImportStatRow } from "../../../api/client.ts";
import SegmentedBar, { type Segment } from "../../charts/SegmentedBar.tsx";
import { formatNumber, percent } from "../../../utils/format.ts";
import Panel from "../../Panel.tsx";
import FigureSections from "./FigureSections.tsx";
import { statRows, sumOf } from "./importStats.ts";

// Job boards with their own import; every other job ad source is an ATS the feed crawler reads.
const BOARDS: { source: string; label: string; color: string }[] = [
  { source: "nav", label: "NAV", color: "var(--mui-palette-primary-main)" },
  { source: "platsbanken", label: "Platsbanken", color: "var(--mui-palette-info-main)" },
  { source: "workable", label: "Workable", color: "var(--mui-palette-warning-main)" },
];

/** Where job ads come from, how many find their employer, and the ATS feeds behind them. */
export default function JobAdsPanel({ stats }: { stats: ImportStatRow[] }) {
  const ads = statRows(stats, "job_ad");
  const feeds = statRows(stats, "ats_feed");
  const boards = new Set(BOARDS.map((b) => b.source));
  const segments: Segment[] = [
    ...BOARDS.map((b) => ({
      key: b.source,
      label: b.label,
      value: sumOf(statRows(stats, "job_ad", [b.source]), "total"),
      color: b.color,
      to: `/job-ads?source=${b.source}`,
    })),
    {
      key: "ats",
      label: "ATS feeds",
      value: sumOf(
        ads.filter((r) => !boards.has(r.source)),
        "total",
      ),
      color: "var(--mui-palette-success-main)",
    },
  ];
  const total = sumOf(ads, "total");

  return (
    <Panel
      title="Job ads"
      subtitle={`${formatNumber(total)} ads · ${percent(sumOf(ads, "matched"), total)} matched to a company`}
      fill
    >
      <Stack spacing={2.5}>
        <SegmentedBar segments={segments} emptyLabel="No job ads yet" />
        <FigureSections
          sections={[
            {
              title: "ATS feeds",
              figures: [
                { label: "Feeds", value: sumOf(feeds, "total"), added: sumOf(feeds, "added_24h") },
                { label: "Verified", value: sumOf(feeds, "matched") },
                { label: "Crawled in 24h", value: sumOf(feeds, "refreshed_24h") },
                {
                  label: "Career pages probed",
                  value: sumOf(statRows(stats, "crawl", ["ats_discovery"]), "total"),
                  added: sumOf(statRows(stats, "crawl", ["ats_discovery"]), "refreshed_24h"),
                },
              ],
            },
          ]}
        />
      </Stack>
    </Panel>
  );
}
